import React, { useMemo, useState } from 'react';
import { Form, FormikProps, withFormik, FormikBag } from 'formik';
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  CardHeader,
  Row,
} from 'reactstrap';
import SubmitButtonWithLoader from 'app/components/common/SubmitButtonWithLoader';
import OtoAutocomplete from 'app/components/common/OtoAutocomplete';
import { citiesById } from 'config';
import i18next from 'i18next';

export type AddCompanyRestaurantFormValues = {
  company_id: number | null;
  restaurant_id: number | null;
};

interface AddCompanyRestaurantFormOwnProps {
  companies: { id: number; name: string; nip: number }[];
  restaurants: { id: number; name: string; city_id: number }[];
  onAddCompanyRestaurant: (values: AddCompanyRestaurantFormValues) => void;
}

type AddCompanyRestaurantFormProps = AddCompanyRestaurantFormOwnProps &
  FormikProps<AddCompanyRestaurantFormValues>;

const AddCompanyRestaurantFormWithoutFormik: React.FC<
  AddCompanyRestaurantFormProps
> = (props) => {
  const {
    isSubmitting,
    setFieldValue,
    companies,
    restaurants,
    values,
  } = props;
  const [selectedCompany, setSelectedCompany] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const companyOptions = useMemo(
    () =>
      companies.map((company) => ({
        id: company.id,
        name: `${company.name} (${company.id}) NIP: ${company.nip}`,
      })),
    [companies]
  );

  const restaurantOptions = useMemo(
    () =>
      restaurants.map((restaurant) => ({
        id: restaurant.id,
        name: `${restaurant.name} (${restaurant.id}) ${
          citiesById[restaurant.city_id]?.name || i18next.t('companies.other-city')
        }`,
      })),
    [restaurants]
  );

  return (
    <Card>
      <CardHeader>{i18next.t('companies.assign-restaurant-title')}</CardHeader>
      <CardBody>
        <Form>
          <Row className="mx-0 d-flex flex-wrap">
            <FormGroup className="col min-w-200">
              <Label>
                {i18next.t('companies.selected-company')}: {values.company_id ?? i18next.t('companies.not-selected')}
              </Label>
              <OtoAutocomplete
                options={companyOptions}
                isSingle={true}
                isObject={true}
                value={selectedCompany}
                onChange={(value) => {
                  if (value) {
                    setSelectedCompany(value as { id: number; name: string });
                    setFieldValue('company_id', value.id);
                  } else {
                    setSelectedCompany(null);
                    setFieldValue('company_id', null);
                  }
                }}
                fieldToDisplay="name"
                placeholder={i18next.t('companies.select-company')}
                style={{
                  searchBox: {
                    background: 'white',
                    marginBottom: '10px',
                  },
                }}
                emptyRecordMsg={i18next.t('companies.no-company-selected')}
              />
            </FormGroup>
            <FormGroup className="col min-w-200">
              <Label>
                {i18next.t('companies.selected-restaurant')}: {values.restaurant_id ?? i18next.t('companies.not-selected')}
              </Label>
              <OtoAutocomplete
                options={restaurantOptions}
                isSingle={true}
                isObject={true}
                value={selectedRestaurant}
                onChange={(value) => {
                  if (value) {
                    setSelectedRestaurant(value as { id: number; name: string });
                    setFieldValue('restaurant_id', value.id);
                  } else {
                    setSelectedRestaurant(null);
                    setFieldValue('restaurant_id', null);
                  }
                }}
                fieldToDisplay="name"
                placeholder={i18next.t('companies.select-restaurant')}
                style={{
                  searchBox: {
                    background: 'white',
                    marginBottom: '10px',
                  },
                }}
                emptyRecordMsg={i18next.t('companies.no-restaurant-selected')}
              />
            </FormGroup>
          </Row>
          <FormGroup className="col-12">
            <SubmitButtonWithLoader
              label={i18next.t('companies.assign-restaurant-title')}
              loading={isSubmitting}
            />
          </FormGroup>
        </Form>
      </CardBody>
    </Card>
  );
};

const AddCompanyRestaurantForm = withFormik<
  AddCompanyRestaurantFormOwnProps,
  AddCompanyRestaurantFormValues
>({
  mapPropsToValues: () => ({
    company_id: null,
    restaurant_id: null,
  }),
  handleSubmit: (
    values: AddCompanyRestaurantFormValues,
    {
      props,
      setSubmitting,
    }: FormikBag<AddCompanyRestaurantFormOwnProps, AddCompanyRestaurantFormValues>
  ) => {
    props.onAddCompanyRestaurant(values);
    setSubmitting(false);
  },
})(AddCompanyRestaurantFormWithoutFormik);

export default AddCompanyRestaurantForm;
